import {
    QUERY_BUILDER_SEARCH_REQUEST, QUERY_BUILDER_SEARCH_SUCCESS, QUERY_BUILDER_SEARCH_ERROR,
    QUERY_BUILDER_RUN_REQUEST, QUERY_BUILDER_RUN_SUCCESS, QUERY_BUILDER_RUN_ERROR, QUERY_BUILDER_RESULTS_CLEAR,
    QUERY_BUILDER_DELETE_REQUEST, QUERY_BUILDER_DELETE_SUCCESS, QUERY_BUILDER_DELETE_ERROR,
    QUERY_BUILDER_MODULE_LABELS_REQUEST, QUERY_BUILDER_MODULE_LABELS_SUCCESS,
    QUERY_BUILDER_RECORD_TYPE_LABELS_REQUEST, QUERY_BUILDER_RECORD_TYPE_LABELS_SUCCESS,
    QUERY_BUILDER_LABELS_ERROR,
    QUERY_EDITOR_CATALOG_REQUEST, QUERY_EDITOR_CATALOG_SUCCESS, QUERY_EDITOR_CATALOG_ERROR,
    QUERY_EDITOR_LOAD_REQUEST, QUERY_EDITOR_LOAD_SUCCESS, QUERY_EDITOR_LOAD_ERROR,
    QUERY_EDITOR_SAVE_REQUEST, QUERY_EDITOR_SAVE_SUCCESS, QUERY_EDITOR_SAVE_ERROR,
    QUERY_EDITOR_TEST_REQUEST, QUERY_EDITOR_TEST_SUCCESS, QUERY_EDITOR_TEST_ERROR, QUERY_EDITOR_TEST_INVALIDATE,
    QUERY_EDITOR_CLEAR
} from './types';

// Catalog lists are keyed by editorCacheKey: absent = not asked, null = in flight, array = loaded.
const initialEditorState = {
    catalog: { modules: {}, recordTypes: {}, fields: {}, comparisonTargets: {}, fieldOptions: {} },
    query: null,
    loading: false,
    saving: false,
    testing: false,
    testResult: null,
    visit: 0,
    testRun: 0,
};

const initialState = {
    queries: [],
    totalCount: 0,
    loading: false,
    error: null,
    deleting: false,
    modulesRequested: false,
    moduleLabels: null,
    recordTypeLabels: {},
    labelsError: null,
    results: null,
    running: false,
    editor: initialEditorState,
};

const withCatalogList = (editor, cache, key, list) => ({
    ...editor,
    catalog: { ...editor.catalog, [cache]: { ...editor.catalog[cache], [key]: list } },
});

/** Drops a failed list so the next request for it retries; the interceptor has already shown why. */
const withoutCatalogList = (editor, cache, key) => {
    const { [key]: _failed, ...rest } = editor.catalog[cache];
    return { ...editor, catalog: { ...editor.catalog, [cache]: rest } };
};

/**
 * Whether a response belongs to an editor visit that has ended, or to a Test the editor has since moved
 * past. Requests carry the visit (and Test its run) they started in, and only a current one may write.
 */
const isObsolete = (editor, meta) =>
    meta != null && (meta.visit !== editor.visit || (meta.testRun != null && meta.testRun !== editor.testRun));

const queryEditorReducer = (editor, { type, payload, meta }) => {
    if (isObsolete(editor, meta)) return editor;
    switch (type) {
        case QUERY_EDITOR_CATALOG_REQUEST:
            return withCatalogList(editor, payload.cache, payload.key, null);
        case QUERY_EDITOR_CATALOG_SUCCESS:
            return withCatalogList(editor, payload.cache, payload.key, payload.items);
        case QUERY_EDITOR_CATALOG_ERROR:
            return withoutCatalogList(editor, payload.cache, payload.key);
        case QUERY_EDITOR_LOAD_REQUEST:
            return { ...editor, loading: true, query: null };
        case QUERY_EDITOR_LOAD_SUCCESS:
            return { ...editor, loading: false, query: payload };
        case QUERY_EDITOR_LOAD_ERROR:
            return { ...editor, loading: false };
        case QUERY_EDITOR_SAVE_REQUEST:
            return { ...editor, saving: true };
        case QUERY_EDITOR_SAVE_SUCCESS:
        case QUERY_EDITOR_SAVE_ERROR:
            return { ...editor, saving: false };
        case QUERY_EDITOR_TEST_REQUEST:
            return { ...editor, testing: true, testResult: null, testRun: editor.testRun + 1 };
        case QUERY_EDITOR_TEST_SUCCESS:
            return { ...editor, testing: false, testResult: payload };
        case QUERY_EDITOR_TEST_ERROR:
            return { ...editor, testing: false };
        case QUERY_EDITOR_TEST_INVALIDATE:
            return { ...editor, testing: false, testResult: null, testRun: editor.testRun + 1 };
        // Every visit starts clean, so nothing loaded for one query or user leaks into the next.
        case QUERY_EDITOR_CLEAR:
            return { ...initialEditorState, visit: editor.visit + 1, testRun: editor.testRun + 1 };
        default:
            return editor;
    }
};

const queryBuilderReducer = (state = initialState, action) => {
    const { type, payload } = action;
    switch (type) {
        case QUERY_BUILDER_SEARCH_REQUEST:
            return { ...state, loading: true, error: null };
        case QUERY_BUILDER_SEARCH_SUCCESS:
            return {
                ...state,
                loading: false,
                queries: payload.results || [],
                totalCount: payload.totalCount || 0,
            };
        case QUERY_BUILDER_SEARCH_ERROR:
            return { ...state, loading: false, error: payload };
        case QUERY_BUILDER_RUN_REQUEST:
            return { ...state, running: true };
        // Results carry the id of the query they came from, so the page can label and clear them.
        case QUERY_BUILDER_RUN_SUCCESS:
            return { ...state, running: false, results: { ...payload.result, queryId: payload.queryId } };
        case QUERY_BUILDER_RUN_ERROR:
            return { ...state, running: false };
        case QUERY_BUILDER_RESULTS_CLEAR:
            return { ...state, running: false, results: null };
        case QUERY_BUILDER_DELETE_REQUEST:
            return { ...state, deleting: true, error: null };
        case QUERY_BUILDER_DELETE_SUCCESS:
            return { ...state, deleting: false };
        case QUERY_BUILDER_DELETE_ERROR:
            return { ...state, deleting: false, error: payload };
        case QUERY_BUILDER_MODULE_LABELS_REQUEST:
            return { ...state, modulesRequested: true };
        case QUERY_BUILDER_MODULE_LABELS_SUCCESS:
            return { ...state, moduleLabels: payload };
        // Marked before the call resolves so a concurrent page load does not ask for the same module.
        case QUERY_BUILDER_RECORD_TYPE_LABELS_REQUEST:
            return { ...state, recordTypeLabels: { ...state.recordTypeLabels, [payload]: {} } };
        case QUERY_BUILDER_RECORD_TYPE_LABELS_SUCCESS:
            return {
                ...state,
                recordTypeLabels: { ...state.recordTypeLabels, [payload.module]: payload.labels },
            };
        // Not retried: the interceptor has already shown the failure, and the grid falls back to keys.
        case QUERY_BUILDER_LABELS_ERROR:
            return { ...state, moduleLabels: state.moduleLabels || {}, labelsError: payload };
        default: {
            const editor = queryEditorReducer(state.editor, action);
            return editor === state.editor ? state : { ...state, editor };
        }
    }
};

export default queryBuilderReducer;
